'use client';

import { useState, useEffect } from 'react';
import { visitCounselApi } from './visit-counsel.api';
import { orderApi } from '@/entities/order';
import type { VisitCounselView } from './visit-counsel.types';
import type { DepartureLocationInfo } from '@/entities/order/model/order.types';

export interface PreviousPlannerCounselResult {
  previousCounsel: VisitCounselView | null;
  previousLocation: DepartureLocationInfo | null;
  isLoading: boolean;
  error: Error | undefined;
}

async function fetchPreviousPlannerCounsel(currentCounselId: string): Promise<{
  previousCounsel: VisitCounselView | null;
  previousLocation: DepartureLocationInfo | null;
}> {
  const current = await visitCounselApi.getVisitCounselById(currentCounselId);
  const { counselorId, scheduleDate, scheduleTime } = current;

  const result = await visitCounselApi.searchVisitCounsels({
    filterGroups: [
      {
        logic: 'AND' as const,
        filters: [
          {
            field: 'counselorId' as const,
            operator: 'EQUALS' as const,
            value: counselorId,
            values: null,
          },
          {
            field: 'scheduleDate' as const,
            operator: 'EQUALS' as const,
            value: scheduleDate,
            values: null,
          },
        ],
      },
    ],
    sortBy: 'scheduleDate' as const,
    sortOrder: 'ASC' as const,
    pageSize: 50,
  });

  const earlierCounsels = result.items
    .filter((c) => c.id !== currentCounselId && c.scheduleTime < scheduleTime)
    .sort((a, b) => (b.scheduleTime > a.scheduleTime ? 1 : -1));

  const previous = earlierCounsels[0] ?? null;
  if (!previous) return { previousCounsel: null, previousLocation: null };

  const order = await orderApi.getOrder(previous.orderId);
  const previousLocation = order.assignedDepartureLocation ?? null;

  return { previousCounsel: previous, previousLocation };
}

/**
 * 해당 플래너의 해당 일자 직전 상담 조회
 * @param currentCounselId 현재 상담 ID (order.status.counsel)
 * @returns 직전 상담 정보 및 해당 주문의 출발지 좌표 (없으면 null)
 */
export function usePreviousPlannerCounsel(
  currentCounselId: string | null
): PreviousPlannerCounselResult {
  const [previousCounsel, setPreviousCounsel] = useState<VisitCounselView | null>(null);
  const [previousLocation, setPreviousLocation] = useState<DepartureLocationInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    if (!currentCounselId) {
      setPreviousCounsel(null);
      setPreviousLocation(null);
      setError(undefined);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(undefined);

    fetchPreviousPlannerCounsel(currentCounselId)
      .then(({ previousCounsel: pc, previousLocation: pl }) => {
        if (!cancelled) {
          setPreviousCounsel(pc);
          setPreviousLocation(pl);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('직전 상담 조회 실패'));
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentCounselId]);

  return {
    previousCounsel,
    previousLocation,
    isLoading,
    error,
  };
}
